import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  BuildingOfficeIcon,
  CheckCircleIcon,
  XCircleIcon,
  MinusCircleIcon,
} from '@heroicons/react/24/outline';

const BusinessStageAssessmentForm = ({ data = {}, onSave, userRole }) => {
  const [assessmentData, setAssessmentData] = useState({
    businessStage: data.businessStage || {},
    businessModel: data.businessModel || {},
    industryContext: data.industryContext || {},
    scalabilityReadiness: data.scalabilityReadiness || {},
    marketPosition: data.marketPosition || {},
    operationalMaturity: data.operationalMaturity || {},
    score: data.score || 0,
    notes: data.notes || '',
  });

  const [saving, setSaving] = useState(false);

  const businessStageQuestions = [
    {
      id: 'businessLifecycle',
      question: 'My business is in the early startup phase (idea validation, MVP development)',
      category: 'businessStage',
    },
    {
      id: 'revenueStage',
      question: 'My business has achieved consistent monthly revenue',
      category: 'businessStage',
    },
    {
      id: 'customerBase',
      question: 'I have a growing customer base with repeat customers',
      category: 'businessStage',
    },
    {
      id: 'teamSize',
      question: 'I have hired employees or built a team beyond myself',
      category: 'businessStage',
    },
    {
      id: 'marketValidation',
      question: 'My business model has been validated by the market',
      category: 'businessStage',
    },
  ];

  const businessModelQuestions = [
    {
      id: 'valueProposition',
      question: 'I have a clear and differentiated value proposition',
      category: 'businessModel',
    },
    {
      id: 'revenueStreams',
      question: 'I have identified and implemented multiple revenue streams',
      category: 'businessModel',
    },
    {
      id: 'customerSegments',
      question: 'I have clearly defined target customer segments',
      category: 'businessModel',
    },
    {
      id: 'costStructure',
      question: 'I understand my cost structure and unit economics',
      category: 'businessModel',
    },
    {
      id: 'competitiveAdvantage',
      question: 'I have identified sustainable competitive advantages',
      category: 'businessModel',
    },
  ];

  const industryContextQuestions = [
    {
      id: 'industryKnowledge',
      question: 'I have deep knowledge of my industry and market dynamics',
      category: 'industryContext',
    },
    {
      id: 'competitiveLandscape',
      question: 'I understand the competitive landscape and key players',
      category: 'industryContext',
    },
    {
      id: 'regulatoryEnvironment',
      question: 'I understand the regulatory environment affecting my business',
      category: 'industryContext',
    },
    {
      id: 'industryTrends',
      question: 'I stay informed about industry trends and disruptions',
      category: 'industryContext',
    },
    {
      id: 'industryNetworks',
      question: 'I have built relationships within my industry',
      category: 'industryContext',
    },
  ];

  const scalabilityReadinessQuestions = [
    {
      id: 'systemsProcesses',
      question: 'I have documented systems and processes in place',
      category: 'scalabilityReadiness',
    },
    {
      id: 'technologyInfrastructure',
      question: 'I have scalable technology infrastructure',
      category: 'scalabilityReadiness',
    },
    {
      id: 'financialResources',
      question: 'I have access to financial resources for growth',
      category: 'scalabilityReadiness',
    },
    {
      id: 'humanResources',
      question: 'I can attract and retain talent for growth',
      category: 'scalabilityReadiness',
    },
    {
      id: 'marketExpansion',
      question: 'I have plans for market expansion and growth',
      category: 'scalabilityReadiness',
    },
  ];

  const marketPositionQuestions = [
    {
      id: 'marketShare',
      question: 'I have gained meaningful market share in my target market',
      category: 'marketPosition',
    },
    {
      id: 'brandRecognition',
      question: 'My brand has recognition in the target market',
      category: 'marketPosition',
    },
    {
      id: 'customerLoyalty',
      question: 'I have built customer loyalty and retention',
      category: 'marketPosition',
    },
    {
      id: 'pricingPower',
      question: 'I have pricing power and can command premium prices',
      category: 'marketPosition',
    },
    {
      id: 'marketLeadership',
      question: 'I am considered a leader or innovator in my market',
      category: 'marketPosition',
    },
  ];

  const operationalMaturityQuestions = [
    {
      id: 'operationalSystems',
      question: 'I have robust operational systems and procedures',
      category: 'operationalMaturity',
    },
    {
      id: 'qualityControl',
      question: 'I have quality control and assurance processes',
      category: 'operationalMaturity',
    },
    {
      id: 'supplyChain',
      question: 'I have reliable supply chain and vendor relationships',
      category: 'operationalMaturity',
    },
    {
      id: 'customerService',
      question: 'I have excellent customer service and support systems',
      category: 'operationalMaturity',
    },
    {
      id: 'performanceMetrics',
      question: 'I track and monitor key performance metrics',
      category: 'operationalMaturity',
    },
  ];

  const handleResponseChange = (category, questionId, response) => {
    setAssessmentData(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [questionId]: response,
      },
    }));
  };

  const calculateScore = () => {
    let totalPoints = 0;
    let maxPoints = 0;

    const categories = [
      'businessStage',
      'businessModel',
      'industryContext',
      'scalabilityReadiness',
      'marketPosition',
      'operationalMaturity',
    ];

    categories.forEach(category => {
      const categoryData = assessmentData[category];
      Object.values(categoryData).forEach(response => {
        if (response === 'stronglyAgree') {
          totalPoints += 4;
        } else if (response === 'agree') {
          totalPoints += 3;
        } else if (response === 'neutral') {
          totalPoints += 2;
        } else if (response === 'disagree') {
          totalPoints += 1;
        }
        maxPoints += 4;
      });
    });

    return Math.round((totalPoints / maxPoints) * 100);
  };

  const handleSave = async () => {
    setSaving(true);
    const updatedData = {
      ...assessmentData,
      score: calculateScore(),
      lastUpdated: new Date().toISOString(),
    };

    try {
      await onSave(updatedData);
    } finally {
      setSaving(false);
    }
  };

  const getScoreColor = score => {
    if (score >= 75) return 'text-green-600';
    if (score >= 60) return 'text-blue-600';
    if (score >= 45) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreStatus = score => {
    if (score >= 75) return 'Mature Business';
    if (score >= 60) return 'Growing Business';
    if (score >= 45) return 'Early Stage Business';
    return 'Startup Phase';
  };

  const renderQuestionSection = (title, questions, category) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
    >
      <h3 className="mb-6 text-xl font-semibold text-gray-900">{title}</h3>

      <div className="space-y-4">
        {questions.map((question, index) => (
          <div key={question.id} className="border-b border-gray-100 pb-4 last:border-b-0">
            <p className="mb-3 text-sm font-medium text-gray-900">
              {index + 1}. {question.question}
            </p>

            <div className="flex flex-wrap gap-2">
              {[
                {
                  value: 'stronglyAgree',
                  label: 'Strongly Agree',
                  icon: CheckCircleIcon,
                  color: 'green',
                },
                { value: 'agree', label: 'Agree', icon: CheckCircleIcon, color: 'blue' },
                { value: 'neutral', label: 'Neutral', icon: MinusCircleIcon, color: 'gray' },
                { value: 'disagree', label: 'Disagree', icon: XCircleIcon, color: 'red' },
              ].map(({ value, label, icon: Icon, color }) => (
                <button
                  key={value}
                  onClick={() => handleResponseChange(category, question.id, value)}
                  className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-medium transition-colors ${
                    assessmentData[category][question.id] === value
                      ? `bg-${color}-100 border-${color}-500 text-${color}-700`
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-orange-200 bg-orange-50 p-6">
        <div className="mb-4 flex items-center gap-3">
          <BuildingOfficeIcon className="h-8 w-8 text-orange-600" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Business Stage Assessment</h2>
            <p className="text-gray-600">
              Evaluate your business maturity, model, and readiness for growth
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-gray-600">Current Score</div>
            <div className={`text-3xl font-bold ${getScoreColor(calculateScore())}`}>
              {calculateScore()}%
            </div>
            <div className="text-sm text-gray-600">{getScoreStatus(calculateScore())}</div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="rounded-lg bg-orange-600 px-6 py-3 font-medium text-white transition-colors hover:bg-orange-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Assessment'}
          </button>
        </div>
      </div>

      {renderQuestionSection('Business Stage & Lifecycle', businessStageQuestions, 'businessStage')}

      {renderQuestionSection('Business Model & Strategy', businessModelQuestions, 'businessModel')}

      {renderQuestionSection(
        'Industry Context & Knowledge',
        industryContextQuestions,
        'industryContext'
      )}

      {renderQuestionSection(
        'Scalability & Growth Readiness',
        scalabilityReadinessQuestions,
        'scalabilityReadiness'
      )}

      {renderQuestionSection('Market Position & Brand', marketPositionQuestions, 'marketPosition')}

      {renderQuestionSection(
        'Operational Maturity',
        operationalMaturityQuestions,
        'operationalMaturity'
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
      >
        <h3 className="mb-4 text-xl font-semibold text-gray-900">Assessment Notes</h3>
        <textarea
          value={assessmentData.notes}
          onChange={e => setAssessmentData(prev => ({ ...prev, notes: e.target.value }))}
          rows={4}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-orange-500 focus:ring-orange-500"
          placeholder="Add any additional observations about your business stage, growth opportunities, or development needs..."
        />
      </motion.div>
    </div>
  );
};

export default BusinessStageAssessmentForm;
